/**
 * 휴일근무일지 API (v2.97.0 · 2026-08-26)
 *
 * ⭐ 목록·상세·결재는 **지출결재와 같은 통로**(expense-api)를 쓴다.
 *   여기 있는 것은 「휴일근무일지에만 있는 것」 — 작성·수정뿐이다.
 */

import type { ApiResponse } from './types';

const BASE = '/api';

export interface HolidayWorker {
  seq: number;
  userId: string | null;
  empId: string | null;
  name: string;
  title: string | null;
  department: string | null;
}

export interface HolidayDetail {
  startDate: string;
  startTime: string;
  endDate: string;
  endTime: string;
  workReason: string;
  workContent: string;
  remark: string;
  workers: HolidayWorker[];
  /** 서버가 계산한 근무 시간 (분). 값이 이상하면 null */
  workMinutes: number | null;
  /** 「10시간 30분」 — 양식 표기 그대로 */
  workTimeText: string;
}

export interface HolidaySaveInput {
  startDate: string;
  startTime: string;
  endDate: string;
  endTime: string;
  workReason: string;
  workContent: string;
  remark: string;
  /** 추가 근무자 계정 — 작성자 본인은 서버가 1번으로 넣는다 */
  extraWorkerIds: string[];
  docDate?: string;
  submit: boolean;
}

async function send<T>(path: string, method: 'POST' | 'PUT', body: unknown): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    method,
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
  });
  const json = (await res.json()) as ApiResponse<T>;
  if (!json.ok) throw new Error(json.error.message);
  return json.data;
}

export function createHolidayDoc(input: HolidaySaveInput): Promise<{ id: number; docNo: string; status: string }> {
  return send('/expense/holiday', 'POST', input);
}

export function updateHolidayDoc(id: number, input: HolidaySaveInput): Promise<{ id: number; status: string }> {
  return send(`/expense/holiday/${id}`, 'PUT', input);
}

/** 「10시간 30분」 — 화면이 입력 중에 미리 보여 줄 때 쓴다(서버 계산과 같은 식). */
export function localWorkTimeText(
  startDate: string, startTime: string, endDate: string, endTime: string,
): string {
  const t = (s: string): number | null => {
    const m = /^([01]\d|2[0-3]):([0-5]\d)$/.exec(s);
    return m ? Number(m[1]) * 60 + Number(m[2]) : null;
  };
  const d = (s: string): number | null => {
    const ms = Date.parse(`${s}T00:00:00Z`);
    return Number.isNaN(ms) ? null : ms;
  };
  const s = t(startTime); const e = t(endTime);
  const ds = d(startDate); const de = d(endDate);
  if (s === null || e === null || ds === null || de === null) return '';
  const min = Math.round((de - ds) / 86_400_000) * 24 * 60 + (e - s);
  if (min <= 0) return '';
  const h = Math.floor(min / 60); const mm = min % 60;
  if (h > 0 && mm > 0) return `${h}시간 ${mm}분`;
  if (h > 0) return `${h}시간`;
  return `${mm}분`;
}
